import { useState, useEffect } from "react";
import {
  Container, Header, Table, Button, SpaceBetween, Box, Badge,
  Modal, FormField, Multiselect, Alert, TextFilter, Pagination,
  StatusIndicator, Input,
} from "@cloudscape-design/components";
import { adminService } from "../../services/adminService";

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  // Group modal state
  const [editUser, setEditUser] = useState(null);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [saving, setSaving] = useState(false);

  // Add user modal state
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUsername, setNewUsername] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newUserGroups, setNewUserGroups] = useState([]);
  const [creating, setCreating] = useState(false);

  // Edit user modal state
  const [editUserTarget, setEditUserTarget] = useState(null);
  const [editEmail, setEditEmail] = useState("");
  const [editPassword, setEditPassword] = useState("");
  const [editing, setEditing] = useState(false);

  // Delete confirmation modal state
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  // Show/hide password for add user form
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Memory modal state
  const [memoryUser, setMemoryUser] = useState(null);
  const [memoryData, setMemoryData] = useState(null);
  const [memoryLoading, setMemoryLoading] = useState(false);
  const [clearingMemory, setClearingMemory] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [u, g] = await Promise.all([adminService.getUsers(), adminService.getGroups()]);
      // Enhance users with group objects
      const enhanced = await Promise.all(u.map(async user => {
        try {
          const userGroups = await adminService.getUserGroups(user.user_id);
          return { ...user, groupObjects: userGroups };
        } catch {
          return { ...user, groupObjects: [] };
        }
      }));
      setUsers(enhanced);
      setGroups(g);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load users.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openEdit = async (user) => {
    setEditUser(user);
    const userGroups = user.groupObjects || [];
    setSelectedGroups(userGroups.map(g => ({ value: g.group_id || g.name, label: g.name })));
  };

  const saveGroups = async () => {
    setSaving(true);
    setError("");
    try {
      await adminService.updateUserGroups(editUser.user_id, selectedGroups.map(g => g.value));
      setSuccess(`Groups updated for ${editUser.email}`);
      setEditUser(null);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to update groups.");
    } finally {
      setSaving(false);
    }
  };

  const filtered = users.filter(u => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return u.email?.toLowerCase().includes(q) || u.display_name?.toLowerCase().includes(q);
  });

  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const groupOptions = groups.map(g => ({ value: g.group_id || g.name, label: g.name, description: g.description }));

  const cols = [
    { id: "email", header: "Email", cell: u => u.email },
    { id: "display_name", header: "Display Name", cell: u => u.display_name || "—" },
    {
      id: "status", header: "Status",
      cell: u => <StatusIndicator type={u.is_active !== false ? "success" : "error"}>
        {u.is_active !== false ? "Active" : "Inactive"}
      </StatusIndicator>,
    },
    {
      id: "groups", header: "Groups",
      cell: u => (u.groupObjects?.length
        ? <SpaceBetween direction="horizontal" size="xs">
            {u.groupObjects.map(g => <Badge key={g.group_id || g.name} color="green">{g.name}</Badge>)}
          </SpaceBetween>
        : <Badge color="grey">No Groups</Badge>),
    },
    { id: "created", header: "Created", cell: u => u.created_at ? new Date(u.created_at).toLocaleDateString() : "—" },
    {
      id: "actions", header: "Actions",
      cell: u => (
        <SpaceBetween direction="horizontal" size="xs">
          <Button size="small" onClick={() => openEdit(u)}>Groups</Button>
          <Button size="small" onClick={() => openEditUser(u)}>Edit</Button>
          <Button size="small" onClick={() => openMemory(u)}>Memory</Button>
          <Button size="small" onClick={() => setDeleteTargetId(u.user_id)}>Delete</Button>
        </SpaceBetween>
      ),
    },
  ];

  const deleteUser = async () => {
    try {
      await adminService.deleteUser(deleteTargetId);
      setSuccess("User deleted.");
      setDeleteTargetId(null);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to delete user.");
      setDeleteTargetId(null);
    }
  };

  const openEditUser = (user) => {
    setEditUserTarget(user);
    setEditEmail(user.email || "");
    setEditPassword("");
  };

  const saveEditUser = async () => {
    if (!editEmail.trim()) {
      setError("Email cannot be empty.");
      return;
    }
    if (editPassword && editPassword.length < 6) {
      setError("New password must be at least 6 characters.");
      return;
    }
    setEditing(true);
    setError("");
    try {
      await adminService.editUser(
        editUserTarget.user_id,
        editEmail.trim() !== editUserTarget.email ? editEmail.trim() : null,
        editPassword || null
      );
      const parts = [];
      if (editEmail.trim() !== editUserTarget.email) parts.push("email updated");
      if (editPassword) parts.push("password reset — user will be prompted to change on next login");
      setSuccess(`User "${editUserTarget.user_id}": ${parts.join(", ") || "no changes"}.`);
      setEditUserTarget(null);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to update user.");
    } finally {
      setEditing(false);
    }
  };

  const openMemory = async (user) => {
    setMemoryUser(user);
    setMemoryData(null);
    setMemoryLoading(true);
    try {
      const data = await adminService.getUserMemory(user.user_id);
      setMemoryData(data);
    } catch {
      setMemoryData({ facts: [], session_count: 0 });
    } finally {
      setMemoryLoading(false);
    }
  };

  const clearMemory = async () => {
    setClearingMemory(true);
    try {
      await adminService.clearUserMemory(memoryUser.user_id);
      setMemoryData({ facts: [], session_count: 0, last_updated: null });
      setSuccess(`Memory cleared for ${memoryUser.user_id}`);
    } catch {
      setError("Failed to clear memory.");
    } finally {
      setClearingMemory(false);
    }
  };

  const openAddUser = () => {
    setNewUsername("");
    setNewEmail("");
    setNewPassword("");
    setNewUserGroups([]);
    setShowNewPassword(false);
    setShowAddUser(true);
  };

  const createUser = async () => {
    if (!newUsername.trim() || !newEmail.trim() || !newPassword.trim()) {
      setError("Username, email, and password are required.");
      return;
    }
    setCreating(true);
    setError("");
    try {
      await adminService.createUser(
        newUsername.trim(),
        newEmail.trim(),
        newPassword,
        newUserGroups.map(g => g.value)
      );
      setSuccess(`User "${newUsername}" created. They will be prompted to change their password on first login.`);
      setShowAddUser(false);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to create user.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Container header={
      <Header
        variant="h2"
        description="Manage users and group assignments"
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={openAddUser} variant="primary">Add User</Button>
            <Button onClick={load} loading={loading}>Refresh</Button>
          </SpaceBetween>
        }
      >User Management</Header>
    }>
      <SpaceBetween size="m">
        {error && <Alert type="error" dismissible onDismiss={() => setError("")}>{error}</Alert>}
        {success && <Alert type="success" dismissible onDismiss={() => setSuccess("")}>{success}</Alert>}

        <Table
          columnDefinitions={cols}
          items={paginated}
          loading={loading}
          loadingText="Loading users..."
          filter={<TextFilter filteringText={filter} onChange={({ detail }) => { setFilter(detail.filteringText); setPage(1); }} filteringPlaceholder="Search by email or name..." />}
          pagination={<Pagination currentPageIndex={page} pagesCount={Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))} onChange={({ detail }) => setPage(detail.currentPageIndex)} />}
          empty={<Box textAlign="center" color="text-body-secondary">{filter ? "No users match filter." : "No users registered yet."}</Box>}
        />

        <Modal
          visible={!!editUser}
          onDismiss={() => setEditUser(null)}
          header={`Manage Groups: ${editUser?.email}`}
          footer={
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setEditUser(null)} disabled={saving}>Cancel</Button>
              <Button variant="primary" onClick={saveGroups} loading={saving}>Update Groups</Button>
            </SpaceBetween>
          }
        >
          <SpaceBetween size="m">
            <FormField label="Groups" description="Select groups for this user">
              <Multiselect
                selectedOptions={selectedGroups}
                onChange={({ detail }) => setSelectedGroups(detail.selectedOptions)}
                options={groupOptions}
                placeholder="Select groups..."
              />
            </FormField>
          </SpaceBetween>
        </Modal>

        <Modal
          visible={showAddUser}
          onDismiss={() => setShowAddUser(false)}
          header="Add New User"
          footer={
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setShowAddUser(false)} disabled={creating}>Cancel</Button>
              <Button variant="primary" onClick={createUser} loading={creating}>Create User</Button>
            </SpaceBetween>
          }
        >
          <SpaceBetween size="m">
            <Alert type="info">
              The user will be required to change their password on first login.
            </Alert>
            <FormField label="Username" description="Unique login username">
              <Input
                value={newUsername}
                onChange={({ detail }) => setNewUsername(detail.value)}
                placeholder="e.g. john.smith"
              />
            </FormField>
            <FormField label="Email">
              <Input
                value={newEmail}
                onChange={({ detail }) => setNewEmail(detail.value)}
                placeholder="e.g. john.smith@company.com"
                type="email"
              />
            </FormField>
            <FormField label="Temporary Password" description="User will be asked to change this on first login">
              <div style={{ position: "relative" }}>
                <Input
                  value={newPassword}
                  onChange={({ detail }) => setNewPassword(detail.value)}
                  placeholder="Minimum 6 characters"
                  type={showNewPassword ? "text" : "password"}
                />
                <div style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)" }}>
                  <Button variant="link" formAction="none" onClick={() => setShowNewPassword(p => !p)}>
                    {showNewPassword ? "Hide" : "Show"}
                  </Button>
                </div>
              </div>
            </FormField>
            <FormField label="Groups (optional)" description="Assign groups now or manage later">
              <Multiselect
                selectedOptions={newUserGroups}
                onChange={({ detail }) => setNewUserGroups(detail.selectedOptions)}
                options={groupOptions}
                placeholder="Select groups..."
              />
            </FormField>
          </SpaceBetween>
        </Modal>
        <Modal
          visible={!!editUserTarget}
          onDismiss={() => setEditUserTarget(null)}
          header={`Edit User: ${editUserTarget?.user_id}`}
          footer={
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setEditUserTarget(null)} disabled={editing}>Cancel</Button>
              <Button variant="primary" onClick={saveEditUser} loading={editing}>Save Changes</Button>
            </SpaceBetween>
          }
        >
          <SpaceBetween size="m">
            <FormField label="Email">
              <Input
                value={editEmail}
                onChange={({ detail }) => setEditEmail(detail.value)}
                placeholder="user@company.com"
                type="email"
              />
            </FormField>
            <FormField
              label="Reset Password"
              description="Leave blank to keep current password. If set, user will be forced to change it on next login."
            >
              <Input
                value={editPassword}
                onChange={({ detail }) => setEditPassword(detail.value)}
                placeholder="New temporary password (min 6 chars)"
                type="password"
              />
            </FormField>
            {editPassword && (
              <Alert type="info">
                User will be prompted to set a new password on their next login.
              </Alert>
            )}
          </SpaceBetween>
        </Modal>
        <Modal
          visible={deleteTargetId !== null}
          onDismiss={() => setDeleteTargetId(null)}
          header="Delete user?"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => setDeleteTargetId(null)}>Cancel</Button>
                <Button variant="primary" onClick={deleteUser}>Delete</Button>
              </SpaceBetween>
            </Box>
          }
        >
          <Box>This will permanently delete the user and all their data. This action cannot be undone.</Box>
        </Modal>

        <Modal
          visible={!!memoryUser}
          onDismiss={() => setMemoryUser(null)}
          header={`DocuSense Memory: ${memoryUser?.user_id}`}
          footer={
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setMemoryUser(null)}>Close</Button>
              <Button variant="primary" onClick={clearMemory} loading={clearingMemory}>Clear Memory</Button>
            </SpaceBetween>
          }
        >
          {memoryLoading ? (
            <Box textAlign="center">Loading...</Box>
          ) : memoryData ? (
            <SpaceBetween size="m">
              <Box color="text-body-secondary" fontSize="body-s">
                {memoryData.session_count || 0} DocuSense sessions contributed · Last updated: {memoryData.last_updated ? new Date(memoryData.last_updated).toLocaleString() : "never"}
              </Box>
              {(memoryData.facts || []).length === 0 ? (
                <Box color="text-body-secondary">No memory yet. Memory builds up after DocuSense conversations.</Box>
              ) : (
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {memoryData.facts.map((f, i) => (
                    <li key={i} style={{ marginBottom: 4 }}>
                      {f.fact} <span style={{ color: "#888", fontSize: 12 }}>({f.mentions}x)</span>
                    </li>
                  ))}
                </ul>
              )}
            </SpaceBetween>
          ) : null}
        </Modal>
      </SpaceBetween>
    </Container>
  );
}
